import { Controller } from "@hotwired/stimulus";

import { LiteGraph, LGraphNode, LGraph, LGraphCanvas } from "@comfyorg/litegraph";
import { formatPhoneNumberForDisplay, normalizePhoneNumberToE164 } from "../lib/phone_number";

// const VOICE_MODELS = [
//   "default",
//   "aura-2-andromeda-en",
//   "aura-2-callista-en",
//   "aura-2-helena-en",
//   "aura-2-luna-en",
//   "aura-2-phoebe-en",
//   "aura-2-aries-en",
//   "aura-2-atlas-en",
//   "aura-2-neptune-en",
//   "aura-2-odysseus-en",
//   "aura-2-orion-en",
// ];
//
// const CONVERSE_MODELS = ["default", "gpt-5-nano", "gpt-5-mini", "gpt-5-chat", "gpt-5"];

const BRANCH_OPERATORS = [
  "equals",
  "notEquals",
  "contains",
  "startsWith",
  "endsWith",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
  "isPresent",
  "isTrue",
  "isFalse",
];
const BRANCH_VALUELESS_OPERATORS = ["isPresent", "isTrue", "isFalse"];
const MAX_ROUTE_CASES = 10;
const SCHEDULE_VALID_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const SCHEDULE_DEFAULT_SELECTED_DAYS = SCHEDULE_VALID_DAYS.slice(0, 5);
const SCHEDULE_HH_MM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const SCHEDULE_DAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};
const SCHEDULE_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = String(Math.floor(index / 2)).padStart(2, "0");
  const minute = index % 2 === 0 ? "00" : "30";
  return `${hour}:${minute}`;
}).filter((time) => SCHEDULE_HH_MM_PATTERN.test(time));
const SCHEDULE_DEFAULT_START_TIME = "09:00";
const SCHEDULE_DEFAULT_END_TIME = "17:00";
const SCHEDULE_DEFAULT_TIMEZONE = "America/Detroit";
const WORKFLOW_TRANSFER_FORMAT = "healpay-assist-workflow";
const WORKFLOW_TRANSFER_FORMAT_VERSION = 1;
const SUPPORTED_WORKFLOW_NODE_KINDS = new Set([
  "global",
  "hangup",
  "say",
  "converse",
  "account",
  "payment",
  "transfer",
  "callGroup",
  "branch",
  "schedule",
  "route",
]);
const SERIALIZED_POSITION_PATTERN =
  /^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?\s*,\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?\s*$/i;
const SCHEDULE_TIMEZONE_OPTIONS = [
  "America/Detroit",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const validScheduleTime = (value) =>
  typeof value === "string" && SCHEDULE_HH_MM_PATTERN.test(value);

const scheduleTimeOptionsFor = (value) => {
  const options = [...SCHEDULE_TIME_OPTIONS];
  if (validScheduleTime(value) && !options.includes(value)) {
    options.push(value);
  }
  return options;
};

const validScheduleTimezone = (value) => {
  if (typeof value !== "string" || !value) {
    return false;
  }

  if (typeof Intl.supportedValuesOf === "function") {
    return Intl.supportedValuesOf("timeZone").includes(value);
  }

  try {
    new Intl.DateTimeFormat(undefined, { timeZone: value }).format(new Date());
    return value.includes("/");
  } catch (_error) {
    return false;
  }
};

const scheduleTimezoneOptionsFor = (value) => {
  const options = [...SCHEDULE_TIMEZONE_OPTIONS];
  if (validScheduleTimezone(value) && !options.includes(value)) {
    options.push(value);
  }
  return options;
};

const normalizedPosition = (position) => {
  const x = position?.[0];
  const y = position?.[1];
  const coordinates = [Number(x), Number(y)];

  return coordinates.every((value) => Number.isFinite(value)) ? coordinates : null;
};

const snapPositionToGrid = (position) => {
  const gridSize = LiteGraph.CANVAS_GRID_SIZE || 1;

  return (normalizedPosition(position) || [0, 0]).map(
    (value) => gridSize * Math.round(value / gridSize),
  );
};

const serializePosition = (position) => {
  return normalizedPosition(position)
    ?.map((value) => Math.round(value))
    .join(",");
};

let facts = [];
let callGroupCatalog = { groups: [], loaded: false };
const shelfNodes = [];
let fullscreenController = null;

LiteGraph.alwaysSnapToGrid = true;
LiteGraph.canvasNavigationMode = "legacy";
LiteGraph.CONNECTING_LINK_COLOR = "#888";

LGraphCanvas.link_type_colors["flow"] = "#3181ac";
LGraphCanvas.link_type_colors["intent"] = "#368e3a";

const factKeysForUse = (use) => {
  return facts
    .filter((fact) => fact?.uses?.includes(use))
    .map((fact) => fact.key)
    .filter((key) => key);
};

const addFactWidget = (node, use) => {
  const factKeys = factKeysForUse(use);
  if (node.properties.fact && !factKeys.includes(node.properties.fact)) {
    factKeys.push(node.properties.fact);
  }

  if (factKeys.length > 0) {
    const value = node.properties.fact || factKeys[0];
    node.properties.fact = value;
    node.addWidget("combo", "Fact", value, "fact", { values: factKeys });
  } else {
    node.addWidget("text", "Fact", node.properties.fact || "", "fact");
  }
};

const shelfLabelFor = (NodeClass) => {
  return NodeClass.title?.replace(/ Node$/, "") || NodeClass.kind;
};

const registerWorkflowNode = (type, NodeClass, options = {}) => {
  LiteGraph.registerNodeType(type, NodeClass);

  if (options.shelf === false) {
    return;
  }

  shelfNodes.push({
    type,
    label: options.label || shelfLabelFor(NodeClass),
    description: options.description || "",
    kind: NodeClass.kind,
    group: options.group || "nodes",
    order: options.order ?? shelfNodes.length,
  });
};

class WorkflowNode extends LGraphNode {
  static title = "UNKNOWN";
  static kind = "unknown";

  constructor(title, type) {
    super(title, type);

    this.resizable = false;
    this.setColorOption(LGraphCanvas.node_colors.pale_blue);

    if (this.title === "Unnamed") {
      this.title = this.constructor.title;
    }
  }

  onConnectOutput(slot, type, _input, _target_node, _target_slot) {
    // Ensure flow outputs can only connect to a single input slot
    if (type !== "flow") {
      return true;
    }

    this.disconnectOutput(slot);

    return true;
  }

  applySerializedOptions(options) {
    for (const [key, value] of Object.entries(options || {})) {
      if (Object.hasOwn(this.properties, key)) {
        this.setProperty(key, value);
      }
    }
  }

  serializeOptions() {
    return this.properties;
  }

  get displayType() {
    return this.title;
  }

  getSlotMenuOptions(_slot) {
    return null;
  }
}

class FlowNode extends WorkflowNode {
  constructor(title, type) {
    super(title, type);

    // Minimum size. Height will with the inputs.
    this.setSize([210, 0]);

    this.addProperty("inputs", 1, "number");
    this.onPropertyChanged("inputs", 1, 0);

    this.addInputButton = this.addTitleButton({
      name: "add",
      text: "➕", // Plus emoji. NOTE: We can do a proper icon, but for now using this.
      xOffset: -8,
    });
    this.delInputButton = this.addTitleButton({
      name: "add",
      text: "➖", // Minus emoji
      xOffset: -12,
    });
  }

  onTitleButtonClick(button, _canvas) {
    if (this.addInputButton === button) {
      this.setProperty("inputs", parseInt(this.properties.inputs) + 1);
    } else if (this.delInputButton === button) {
      this.setProperty("inputs", parseInt(this.properties.inputs) - 1);
    }
  }

  onPropertyChanged(name, valueRaw, _prev_value) {
    if (name === "inputs") {
      let value = parseInt(valueRaw, 10);
      value = Math.min(Math.max(value, 1), 10);
      this.properties.inputs = value;

      for (let i = this.inputs.length; i < value; i++) {
        this.addInput("in", "flow");
      }
      for (let i = this.inputs.length; i > value; i--) {
        this.removeInput(this.inputs.length - 1);
      }
      this.setSize([this.size[0], 0]);
      this.expandToFitContent();
    }

    return true;
  }
}

class GlobalNode extends WorkflowNode {
  static title = "Global Node";
  static shape = LiteGraph.BOX_SHAPE;
  static kind = "global";

  constructor(title, type) {
    super(title, type);

    this.setColorOption(LGraphCanvas.node_colors.purple);

    this.addOutput("start", "flow", { label: "Start" });
    this.addOutput("match", "intent", { label: "Match" });
    this.addOutput("idleTimeout", "flow", { label: "Idle Timeout" });
    this.addOutput("callTimeout", "flow", { label: "Call Timeout" });
  }
}
registerWorkflowNode("nodes/global", GlobalNode, {
  description: "Starts the call and connects fallback paths.",
  group: "global",
  order: 0,
});

class Intent extends WorkflowNode {
  static title = "Intent";
  static shape = LiteGraph.ROUND_SHAPE;
  static kind = "intent";

  constructor(title, type) {
    super(title, type);

    this.setColorOption(LGraphCanvas.node_colors.green);

    this.addInput("when", "intent");
    this.addOutput("next", "flow");

    this.addProperty("intent", "", "string");
    this.addWidget("text", "intent", "", "intent");
  }
}
registerWorkflowNode("special/intent", Intent, {
  description: "Matches what the caller wants.",
  group: "intent",
  order: 10,
});

class MessageNode extends FlowNode {
  static shape = LiteGraph.CARD_SHAPE;

  constructor(title, type) {
    super(title, type);

    this.addProperty("voiceModel", "default", "string");
    // NOTE: Disabling for now to keep things simpler.
    // this.addWidget("combo", "Voice", "default", null, {
    //   values: VOICE_MODELS,
    //   property: "voiceModel",
    // });

    this.addProperty("message", "", "string");
    this.addWidget("text", "Message", "", "message", { multiline: true });
  }
}

class HangupNode extends FlowNode {
  static title = "Hangup Node";
  static kind = "hangup";

  constructor(title, type) {
    super(title, type);
    this.setColorOption(LGraphCanvas.node_colors.red);
  }
}
registerWorkflowNode("nodes/hangup", HangupNode, {
  description: "Ends the call.",
  order: 90,
});

class SayNode extends MessageNode {
  static title = "Say Node";
  static kind = "say";

  constructor(title, type) {
    super(title, type);

    this.addOutput("next", "flow");
  }
}
registerWorkflowNode("nodes/say", SayNode, {
  description: "Speaks a fixed message, then moves to the next node.",
  order: 30,
});

class ConverseNode extends MessageNode {
  static title = "Converse Node";
  static kind = "converse";

  constructor(title, type) {
    super(title, type);

    this.addOutput("match", "intent", { label: "match" });

    this.addProperty("converseModel", "default", "string");
    // NOTE: Disabling for now to keep things simpler.
    // this.addWidget("combo", "Model", "default", null, {
    //   values: CONVERSE_MODELS,
    //   property: "converseModel",
    // });

    this.addProperty("prompt", "", "string");
    this.addWidget("text", "Prompt", "", "prompt", { multiline: true });

    this.addProperty("maskNumbers", true, "boolean");
    this.addWidget("toggle", "Mask Numbers", true, null, { property: "maskNumbers" });
  }
}
registerWorkflowNode("nodes/converse", ConverseNode, {
  description: "Talks with the caller using configured instructions.",
  order: 20,
});

class AccountNode extends SayNode {
  static title = "Account Node";
  static kind = "account";

  constructor(title, type) {
    super(title, type);

    this.addProperty("dataless", true, "boolean");
    this.addWidget("toggle", "Dataless", true, null, { property: "dataless" });
    this.addOutput("fail", "flow");
  }
}
registerWorkflowNode("nodes/account", AccountNode, {
  description: "Looks up account information and publishes account facts.",
  order: 40,
});

class PaymentNode extends FlowNode {
  static title = "Payment Node";
  static shape = LiteGraph.CARD_SHAPE;
  static kind = "payment";

  constructor(title, type) {
    super(title, type);

    this.addOutput("next", "flow");

    this.addProperty("voiceModel", "default", "string");
    // NOTE: Disabling for now to keep things simpler.
    // this.addWidget("combo", "Voice", "default", null, {
    //   values: VOICE_MODELS,
    //   property: "voiceModel",
    // });
    this.addOutput("fail", "flow");
  }
}
registerWorkflowNode("nodes/payment", PaymentNode, {
  description: "Collects a payment and publishes payment facts.",
  order: 50,
});

class TransferNode extends FlowNode {
  static title = "Transfer Node";
  static shape = LiteGraph.CARD_SHAPE;
  static kind = "transfer";

  constructor(title, type) {
    super(title, type);

    this.setColorOption(LGraphCanvas.node_colors.red);

    this.addProperty("phone", "", "string");
    this.phoneWidget = this.addWidget(
      "text",
      "Phone",
      "",
      (value, _canvas, node) => {
        const normalized = normalizePhoneNumberToE164(value);
        node.setProperty("phone", normalized || String(value || "").trim());
        this.phoneWidget.value = normalized ? formatPhoneNumberForDisplay(normalized) : value;
      },
      { property: "phone" },
    );
  }

  applySerializedOptions(options) {
    const phone = normalizePhoneNumberToE164(options?.phone) || options?.phone;
    super.applySerializedOptions({ ...options, phone });
    this.phoneWidget.value = formatPhoneNumberForDisplay(this.properties.phone);
  }

  serializeOptions() {
    return {
      ...this.properties,
      phone: normalizePhoneNumberToE164(this.properties.phone) || this.properties.phone,
    };
  }
}
registerWorkflowNode("nodes/transfer", TransferNode, {
  description: "Sends the caller to a configured phone number.",
  order: 80,
});

const CALL_GROUP_DEFAULT_MESSAGE = "Incoming call from {{ caller }}. Press 1 to accept.";
const CALL_GROUP_CALLER_ID_CHOICES = [
  { label: "Caller", value: "caller" },
  { label: "Assist", value: "assist" },
];
const CALL_GROUP_DTMF_KEYS = Array.from({ length: 10 }, (_, index) => String(index));
const CALL_GROUP_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CALL_GROUP_LIQUID_DELIMITER_PATTERN = /{{|}}|{%|%}/;

const shortenedCallGroupId = (id) => String(id).slice(0, 8);

const callGroupChoicesFor = (selectedId) => {
  const nameCounts = new Map();
  for (const group of callGroupCatalog.groups) {
    nameCounts.set(group.name, (nameCounts.get(group.name) || 0) + 1);
  }

  const choices = callGroupCatalog.groups.map((group) => ({
    label:
      nameCounts.get(group.name) > 1
        ? `${group.name} (${shortenedCallGroupId(group.id)})`
        : group.name,
    value: group.id,
  }));

  if (selectedId && !choices.some((choice) => choice.value === selectedId)) {
    choices.push({
      label: `Unavailable call group (${shortenedCallGroupId(selectedId)})`,
      value: selectedId,
    });
  }

  return [{ label: "Select a call group", value: "" }, ...choices];
};

class CallGroupNode extends FlowNode {
  static title = "Call Group Node";
  static shape = LiteGraph.CARD_SHAPE;
  static kind = "callGroup";

  constructor(title, type) {
    super(title, type);

    this.setColorOption(LGraphCanvas.node_colors.red);

    this.addOutput("unanswered", "flow", { label: "Unanswered" });

    this.addProperty("callGroupId", "", "string");
    this.addProperty("timeoutSeconds", 20, "number");
    this.addProperty("callerId", "caller", "string");
    this.addProperty("acceptanceScreeningEnabled", true, "boolean");
    this.addProperty("acceptanceScreeningKey", "1", "string");
    this.addProperty("acceptanceScreeningMessage", CALL_GROUP_DEFAULT_MESSAGE, "string");

    this.syncCallGroupWidgets();
  }

  applySerializedOptions(options) {
    if (Object.hasOwn(options || {}, "inputs")) {
      this.setProperty("inputs", options.inputs);
    }
    this.setProperty(
      "callGroupId",
      Object.hasOwn(options || {}, "callGroupId") ? options.callGroupId : "",
    );
    this.setProperty(
      "timeoutSeconds",
      Object.hasOwn(options || {}, "timeoutSeconds") ? options.timeoutSeconds : null,
    );
    this.setProperty(
      "callerId",
      Object.hasOwn(options || {}, "callerId") ? options.callerId : null,
    );

    const screening = options?.acceptanceScreening;
    if (screening && typeof screening === "object" && !Array.isArray(screening)) {
      this.setProperty(
        "acceptanceScreeningEnabled",
        Object.hasOwn(screening, "enabled") ? screening.enabled : true,
      );
      this.setProperty(
        "acceptanceScreeningKey",
        Object.hasOwn(screening, "key") ? screening.key : "",
      );
      this.setProperty(
        "acceptanceScreeningMessage",
        Object.hasOwn(screening, "message") ? screening.message : "",
      );
    } else {
      this.setProperty("acceptanceScreeningEnabled", null);
      this.setProperty("acceptanceScreeningKey", "");
      this.setProperty("acceptanceScreeningMessage", "");
    }

    this.syncCallGroupWidgets();
  }

  serializeOptions() {
    return {
      inputs: this.properties.inputs,
      callGroupId: this.properties.callGroupId,
      timeoutSeconds: this.properties.timeoutSeconds,
      callerId: this.properties.callerId,
      acceptanceScreening: {
        enabled: this.properties.acceptanceScreeningEnabled,
        key: this.properties.acceptanceScreeningKey,
        message: this.properties.acceptanceScreeningMessage,
      },
    };
  }

  syncCallGroupWidgets() {
    for (const widget of [...(this.widgets || [])]) {
      if (widget.options?.callGroupWidget) {
        this.removeWidget(widget);
      }
    }

    const groupChoices = callGroupChoicesFor(this.properties.callGroupId);
    const selectedGroup =
      groupChoices.find((choice) => choice.value === this.properties.callGroupId) ||
      groupChoices[0];
    this.addWidget(
      "combo",
      "Call Group",
      selectedGroup.label,
      (label) => {
        const choice = groupChoices.find((candidate) => candidate.label === label);
        if (choice) {
          this.setProperty("callGroupId", choice.value);
        }
      },
      { callGroupWidget: true, values: groupChoices.map((choice) => choice.label) },
    );

    const timeoutWidget = this.addWidget(
      "number",
      "Timeout",
      this.properties.timeoutSeconds,
      (value) => {
        const integerValue = Math.round(value);
        timeoutWidget.value = integerValue;
        this.setProperty("timeoutSeconds", integerValue);
      },
      {
        callGroupWidget: true,
        min: 5,
        max: 120,
        precision: 0,
        step2: 1,
      },
    );

    const callerIdChoices = [...CALL_GROUP_CALLER_ID_CHOICES];
    let selectedCallerId = callerIdChoices.find(
      (choice) => choice.value === this.properties.callerId,
    );
    if (!selectedCallerId) {
      selectedCallerId = {
        label: this.properties.callerId == null ? "Select caller ID" : "Invalid caller ID",
        value: this.properties.callerId,
      };
      callerIdChoices.unshift(selectedCallerId);
    }
    this.addWidget(
      "combo",
      "Caller ID",
      selectedCallerId.label,
      (label) => {
        const choice = callerIdChoices.find((candidate) => candidate.label === label);
        if (choice) {
          this.setProperty("callerId", choice.value);
        }
      },
      {
        callGroupWidget: true,
        values: callerIdChoices.map((choice) => choice.label),
      },
    );

    this.addWidget(
      "toggle",
      "Screen Calls",
      Boolean(this.properties.acceptanceScreeningEnabled),
      null,
      { callGroupWidget: true, property: "acceptanceScreeningEnabled" },
    );
    this.addWidget(
      "combo",
      "Acceptance Key",
      this.properties.acceptanceScreeningKey,
      "acceptanceScreeningKey",
      { callGroupWidget: true, values: CALL_GROUP_DTMF_KEYS },
    );
    this.addWidget(
      "text",
      "Acceptance Message",
      this.properties.acceptanceScreeningMessage,
      "acceptanceScreeningMessage",
      { callGroupWidget: true, multiline: true },
    );

    this.setSize([this.size[0], 0]);
    this.expandToFitContent();
  }
}
registerWorkflowNode("nodes/callGroup", CallGroupNode, {
  description: "Offers the caller to a Call Group and routes unanswered attempts.",
  order: 79,
});

class BranchNode extends FlowNode {
  static title = "Branch Node";
  static shape = LiteGraph.CARD_SHAPE;
  static kind = "branch";

  constructor(title, type) {
    super(title, type);

    this.addOutput("then", "flow", { label: "Then" });
    this.addOutput("else", "flow", { label: "Else" });

    this.addProperty("fact", "", "string");
    addFactWidget(this, "branch");

    this.addProperty("operator", BRANCH_OPERATORS[0], "string");
    this.addWidget("combo", "Operator", BRANCH_OPERATORS[0], "operator", {
      values: BRANCH_OPERATORS,
    });

    this.addProperty("value", "", "string");
    this.addWidget("text", "Value", "", "value");
  }

  serializeOptions() {
    const options = { ...this.properties };

    if (BRANCH_VALUELESS_OPERATORS.includes(options.operator)) {
      delete options.value;
    }

    return options;
  }
}
registerWorkflowNode("nodes/branch", BranchNode, {
  description: "Checks one fact with one condition and chooses Then or Else.",
  order: 60,
});

class ScheduleNode extends FlowNode {
  static title = "Schedule Node";
  static shape = LiteGraph.CARD_SHAPE;
  static kind = "schedule";

  constructor(title, type) {
    super(title, type);

    this.addOutput("active", "flow", { label: "Active" });
    this.addOutput("inactive", "flow", { label: "Inactive" });

    this.addProperty("startTime", SCHEDULE_DEFAULT_START_TIME, "string");
    this.addProperty("endTime", SCHEDULE_DEFAULT_END_TIME, "string");
    for (const day of SCHEDULE_VALID_DAYS) {
      this.addProperty(day, SCHEDULE_DEFAULT_SELECTED_DAYS.includes(day), "boolean");
    }
    this.addProperty("timezone", SCHEDULE_DEFAULT_TIMEZONE, "string");

    this.syncScheduleWidgets();
  }

  applySerializedOptions(options) {
    if (Object.hasOwn(options || {}, "inputs")) {
      this.setProperty("inputs", options.inputs);
    }
    if (Object.hasOwn(options || {}, "startTime")) {
      this.setProperty("startTime", options.startTime);
    }
    if (Object.hasOwn(options || {}, "endTime")) {
      this.setProperty("endTime", options.endTime);
    }
    const serializedDays = options?.days;
    const days = Array.isArray(serializedDays) ? serializedDays : [];
    const validLoadedDays =
      Array.isArray(serializedDays) && days.every((day) => SCHEDULE_VALID_DAYS.includes(day));
    for (const day of SCHEDULE_VALID_DAYS) {
      this.setProperty(day, validLoadedDays && days.includes(day));
    }

    if (Object.hasOwn(options || {}, "timezone")) {
      this.setProperty("timezone", options.timezone);
    } else {
      this.setProperty("timezone", "");
    }

    this.syncScheduleWidgets();
  }

  serializeOptions() {
    return {
      inputs: this.properties.inputs,
      startTime: this.properties.startTime,
      endTime: this.properties.endTime,
      days: SCHEDULE_VALID_DAYS.filter((day) => this.properties[day]),
      timezone: this.properties.timezone,
    };
  }

  syncScheduleWidgets() {
    for (const widget of [...(this.widgets || [])]) {
      if (widget.options?.scheduleWidget) {
        this.removeWidget(widget);
      }
    }

    this.addWidget("combo", "Start", this.properties.startTime, "startTime", {
      scheduleWidget: true,
      values: scheduleTimeOptionsFor(this.properties.startTime),
    });
    this.addWidget("combo", "End", this.properties.endTime, "endTime", {
      scheduleWidget: true,
      values: scheduleTimeOptionsFor(this.properties.endTime),
    });
    this.addWidget("combo", "Timezone", this.properties.timezone, "timezone", {
      scheduleWidget: true,
      values: scheduleTimezoneOptionsFor(this.properties.timezone),
    });

    for (const day of SCHEDULE_VALID_DAYS) {
      this.addWidget("toggle", SCHEDULE_DAY_LABELS[day], Boolean(this.properties[day]), null, {
        property: day,
        scheduleWidget: true,
      });
    }
    this.setSize([this.size[0], 0]);
    this.expandToFitContent();
  }
}
registerWorkflowNode("nodes/schedule", ScheduleNode, {
  description: "Routes calls by active hours to Active or Inactive.",
  order: 75,
});

class RouteNode extends FlowNode {
  static title = "Route Node";
  static shape = LiteGraph.CARD_SHAPE;
  static kind = "route";

  constructor(title, type) {
    super(title, type);

    this.addOutput("default", "flow", { label: "Default" });

    this.addProperty("fact", "", "string");
    addFactWidget(this, "route");

    this.addProperty("cases", 0, "number");
    for (let i = 0; i < MAX_ROUTE_CASES; i++) {
      this.addProperty(`case${i}`, "", "string");
    }

    this.syncCases();
  }

  addRouteCase() {
    if (parseInt(this.properties.cases, 10) >= MAX_ROUTE_CASES) {
      return;
    }
    this.setProperty("cases", parseInt(this.properties.cases, 10) + 1);
  }

  removeRouteCase() {
    if (parseInt(this.properties.cases, 10) <= 0) {
      return;
    }
    this.setProperty("cases", parseInt(this.properties.cases, 10) - 1);
  }

  onPropertyChanged(name, valueRaw, prevValue) {
    const handled = super.onPropertyChanged(name, valueRaw, prevValue);

    if (name === "cases") {
      let value = parseInt(valueRaw, 10);
      if (Number.isNaN(value)) {
        value = 0;
      }

      this.properties.cases = Math.min(Math.max(value, 0), MAX_ROUTE_CASES);
      this.syncCases();
    }

    return handled;
  }

  applySerializedOptions(options) {
    const cases = options?.cases || {};

    if (Object.hasOwn(options || {}, "fact")) {
      this.setProperty("fact", options.fact);
    }

    let caseCount = 0;
    for (let i = 0; i < MAX_ROUTE_CASES; i++) {
      const key = String(i);
      const property = `case${i}`;

      if (Object.hasOwn(cases, key)) {
        this.setProperty(property, cases[key]);
        caseCount = i + 1;
      } else {
        this.setProperty(property, "");
      }
    }

    if (Object.hasOwn(options || {}, "inputs")) {
      this.setProperty("inputs", options.inputs);
    }
    this.setProperty("cases", caseCount);
  }

  serializeOptions() {
    const cases = {};

    for (let i = 0; i < parseInt(this.properties.cases, 10); i++) {
      const value = this.properties[`case${i}`];
      if (value !== undefined) {
        cases[String(i)] = value;
      }
    }

    return {
      inputs: this.properties.inputs,
      fact: this.properties.fact,
      cases,
    };
  }

  syncCases() {
    this.syncCaseOutputs();
    this.syncCaseWidgets();
    this.setSize([this.size[0], 0]);
    this.expandToFitContent();
  }

  syncCaseOutputs() {
    const cases = parseInt(this.properties.cases, 10);

    if (this.outputs.length === 0 || this.outputs[0].name !== "default") {
      this.addOutput("default", "flow", { label: "Default" });
    }

    for (let i = this.outputs.length - 1; i > cases; i--) {
      this.removeOutput(i);
    }

    for (let i = 0; i < cases; i++) {
      const slot = i + 1;
      const output = this.outputs[slot];

      if (output) {
        output.name = String(i);
        output.label = String(i + 1);
      } else {
        this.addOutput(String(i), "flow", { label: String(i + 1) });
      }
    }
  }

  syncCaseWidgets() {
    const cases = parseInt(this.properties.cases, 10);

    for (const widget of [...(this.widgets || [])]) {
      if (widget.options?.routeCase || widget.options?.routeCaseButton) {
        this.removeWidget(widget);
      }
    }

    for (let i = 0; i < cases; i++) {
      this.addWidget("text", String(i + 1), this.properties[`case${i}`] || "", `case${i}`, {
        routeCase: true,
      });
    }

    this.addWidget("button", "Add match", null, () => this.addRouteCase(), {
      read_only: cases >= MAX_ROUTE_CASES,
      routeCaseButton: true,
    });
    this.addWidget("button", "Remove match", null, () => this.removeRouteCase(), {
      read_only: cases <= 0,
      routeCaseButton: true,
    });
  }
}
registerWorkflowNode("nodes/route", RouteNode, {
  description: "Checks one fact against several values and chooses the matching path.",
  order: 70,
});

export default class extends Controller {
  static targets = [
    "canvas",
    "collapsedShelf",
    "container",
    "data",
    "discardDraftButton",
    "exportDialog",
    "exportTextarea",
    "exportVersionButton",
    "alert",
    "fullscreenButton",
    "importDialog",
    "importTextarea",
    "importValidationMessage",
    "placementHint",
    "name",
    "shelf",
    "shelfButton",
    "shelfList",
    "saveDraftButton",
    "saveStatus",
    "statusChip",
    "publishButton",
    "startingPointDialog",
    "startingPointEmptyButton",
    "startingPointTemplateButton",
    "versionsDialog",
    "versionsEmpty",
    "versionsList",
    "versionPreviewFullscreenButton",
    "versionPreviewPanel",
    "versionPreviewTitle",
    "restoreVersionButton",
    "copyFeedback",
    "copyJsonButton",
    "useJsonButton",
  ];
  static values = {
    callGroups: Array,
    callGroupsLoaded: Boolean,
    draftUrl: String,
    facts: Array,
    showStartingPoint: Boolean,
    templates: Array,
    preview: Boolean,
    publishUrl: String,
    readonly: Boolean,
    versionsUrl: String,
    versionId: String,
    versionPublishedAt: String,
    versionRevision: Number,
    versionStatus: String,
    versionUpdatedAt: String,
    workflowId: String,
  };

  armNode(event) {
    event.preventDefault();
    if (this.readonlyValue) {
      return;
    }

    const button = event.currentTarget;
    const nodeType = button.dataset.nodeType;
    const nodeTitle = button.dataset.nodeTitle;

    if (this.armedShelfNode?.type === nodeType) {
      this.cancelPlacement();
      return;
    }

    this.armedShelfNode = { type: nodeType, title: nodeTitle };
    this.shelfOpen = false;
    this.updateShelfDisplay();
  }

  openShelf(event) {
    event?.preventDefault();
    if (this.readonlyValue || !this.hasShelfTarget) {
      return;
    }

    this.renderNodeShelf();
    this.shelfOpen = true;
    this.updateShelfDisplay();
  }

  collapseShelf(event) {
    event?.preventDefault();
    if (this.readonlyValue) {
      return;
    }

    this.shelfOpen = false;
    this.updateShelfDisplay();
  }

  renderNodeShelf() {
    if (!this.hasShelfListTarget) {
      return;
    }

    this.shelfListTarget.replaceChildren();

    const sortedShelfNodes = [...shelfNodes].sort((a, b) => a.order - b.order);
    let previousGroup = null;

    for (const node of sortedShelfNodes) {
      if (previousGroup && previousGroup !== node.group) {
        const divider = document.createElement("div");
        divider.className = "workflow-editor-node-shelf__divider";
        this.shelfListTarget.appendChild(divider);
      }

      const button = document.createElement("button");
      button.type = "button";
      button.textContent = node.label;
      button.dataset.action = "click->workflow-editor#armNode";
      button.dataset.nodeType = node.type;
      button.dataset.nodeTitle = node.label;
      button.dataset.workflowEditorTarget = "shelfButton";
      button.className = "workflow-editor-node-shelf__node";
      button.title = node.description || node.label;
      this.shelfListTarget.appendChild(button);

      previousGroup = node.group;
    }
  }

  updateShelfDisplay() {
    if (!this.hasShelfTarget || !this.hasCollapsedShelfTarget) {
      return;
    }

    if (this.readonlyValue || this.previewValue) {
      this.shelfTarget.classList.add("hidden");
      this.collapsedShelfTarget.classList.add("hidden");
      if (this.hasPlacementHintTarget) this.placementHintTarget.classList.add("hidden");
      return;
    }

    this.shelfTarget.classList.toggle("hidden", !this.shelfOpen);
    this.collapsedShelfTarget.classList.toggle("hidden", this.shelfOpen);

    if (this.armedShelfNode && this.hasPlacementHintTarget) {
      this.placementHintTarget.textContent = `Placing ${this.armedShelfNode.title}. Click the canvas to place. Right-click or Esc to cancel.`;
      this.placementHintTarget.classList.remove("hidden");
    } else if (this.hasPlacementHintTarget) {
      this.placementHintTarget.textContent = "";
      this.placementHintTarget.classList.add("hidden");
    }

    for (const button of this.shelfButtonTargets) {
      button.classList.toggle("active", this.armedShelfNode?.type === button.dataset.nodeType);
    }
  }

  handleCanvasPointerDown(event) {
    if (this.readonlyValue) {
      return;
    }

    if (!this.armedShelfNode) {
      return;
    }

    if (event.button === 2) {
      this.cancelPlacement();
      return;
    }

    if (event.button !== 0) {
      return;
    }

    this.placeArmedNode(event);
  }

  handleCanvasContextMenu(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape" && this.versionPreviewFullscreen) {
      event.preventDefault();
      this.exitVersionPreviewFullscreen();
      return;
    }

    if (event.key === "Escape" && this.fullscreen) {
      event.preventDefault();
      this.exitFullscreen();
      return;
    }

    if (this.readonlyValue) {
      return;
    }

    if (event.key !== "Escape" || !this.armedShelfNode) {
      return;
    }

    event.preventDefault();
    this.cancelPlacement();
  }

  placeArmedNode(event) {
    if (this.readonlyValue) {
      return;
    }

    const node = LiteGraph.createNode(this.armedShelfNode.type);
    if (!node) {
      console.error(`Can't create ${this.armedShelfNode.type}`);
      this.cancelPlacement();
      return;
    }

    node.pos = snapPositionToGrid(this.canvas.convertEventToCanvasOffset(event));
    this.graph.add(node);

    this.canvas.dirty_bgcanvas = true;
    this.canvas.dirty_canvas = true;
    this.canvas.draw(true, true);

    this.finishPlacement();
  }

  disarmPlacement() {
    this.armedShelfNode = null;
  }

  finishPlacement() {
    this.disarmPlacement();
    this.shelfOpen = false;
    this.updateShelfDisplay();
  }

  cancelPlacement() {
    this.disarmPlacement();
    if (!this.readonlyValue) {
      this.openShelf();
    }
  }

  resize() {
    if (!this.canvas) {
      return;
    }

    const { width, height } = this.containerTarget.getBoundingClientRect();
    const cssWidth = Math.max(1, Math.round(width));
    const cssHeight = Math.max(1, Math.round(height));
    const ratio = Math.max(1, window.devicePixelRatio || 1);
    const backingWidth = Math.round(cssWidth * ratio);
    const backingHeight = Math.round(cssHeight * ratio);

    this.canvas.canvas.style.width = `${cssWidth}px`;
    this.canvas.canvas.style.height = `${cssHeight}px`;

    this.canvas.resize(backingWidth, backingHeight);
    this.canvas.setDirty(true, true);
    this.canvas.draw(true, true);
  }

  recoverCanvasInteraction() {
    if (!this.canvas) {
      return;
    }

    this.canvas.processMouseCancel();
    this.canvas.setDirty(true, true);
  }

  handleDocumentVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.recoverCanvasInteraction();
    }
  }

  toggleFullscreen(event) {
    event?.preventDefault();

    if (this.fullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  enterFullscreen() {
    this.recoverCanvasInteraction();

    if (this.versionPreviewFullscreen) {
      this.exitVersionPreviewFullscreen();
    }

    if (fullscreenController && fullscreenController !== this) {
      fullscreenController.exitFullscreen();
    }

    fullscreenController = this;
    this.fullscreen = true;
    this.element.classList.add("workflow-editor--fullscreen");
    document.body.classList.add("workflow-editor-fullscreen-lock");
    this.updateFullscreenButton();
    this.resizeAfterLayoutChange();
  }

  exitFullscreen() {
    if (!this.fullscreen) {
      return;
    }

    this.recoverCanvasInteraction();

    this.fullscreen = false;
    this.element.classList.remove("workflow-editor--fullscreen");
    if (fullscreenController === this) {
      fullscreenController = null;
      document.body.classList.remove("workflow-editor-fullscreen-lock");
    }
    this.updateFullscreenButton();
    this.resizeAfterLayoutChange();
  }

  updateFullscreenButton() {
    if (!this.hasFullscreenButtonTarget) {
      return;
    }

    const action = this.fullscreen ? "Collapse" : "Expand";
    const subject = this.previewValue ? "workflow preview" : "workflow editor";
    const label = `${action} ${subject}`;

    this.fullscreenButtonTarget.title = label;
    this.fullscreenButtonTarget.setAttribute("aria-label", label);
    this.fullscreenButtonTarget.dataset.fullscreen = this.fullscreen ? "true" : "false";
  }

  resizeAfterLayoutChange() {
    window.requestAnimationFrame(() => {
      this.resize();
      window.requestAnimationFrame(() => this.resize());
    });
  }

  toggleVersionPreviewFullscreen(event) {
    event?.preventDefault();

    if (this.versionPreviewFullscreen) {
      this.exitVersionPreviewFullscreen();
    } else {
      this.enterVersionPreviewFullscreen();
    }
  }

  enterVersionPreviewFullscreen() {
    if (!this.hasVersionPreviewPanelTarget) {
      return;
    }

    this.recoverCanvasInteraction();

    if (this.fullscreen) {
      this.exitFullscreen();
    }

    if (fullscreenController && fullscreenController !== this) {
      fullscreenController.exitFullscreen();
    }

    fullscreenController = this;
    this.versionPreviewFullscreen = true;
    this.versionPreviewPanelTarget.classList.add(
      "workflow-editor--fullscreen",
      "workflow-versions-dialog__preview--fullscreen",
    );
    document.body.classList.add("workflow-editor-fullscreen-lock");
    this.updateVersionPreviewFullscreenButton();
    this.resizePreviewAfterLayoutChange();
  }

  exitVersionPreviewFullscreen() {
    if (!this.versionPreviewFullscreen || !this.hasVersionPreviewPanelTarget) {
      return;
    }

    this.recoverCanvasInteraction();

    this.versionPreviewFullscreen = false;
    this.versionPreviewPanelTarget.classList.remove(
      "workflow-editor--fullscreen",
      "workflow-versions-dialog__preview--fullscreen",
    );
    if (fullscreenController === this) {
      fullscreenController = null;
      document.body.classList.remove("workflow-editor-fullscreen-lock");
    }
    this.updateVersionPreviewFullscreenButton();
    this.resizePreviewAfterLayoutChange();
  }

  updateVersionPreviewFullscreenButton() {
    if (!this.hasVersionPreviewFullscreenButtonTarget) {
      return;
    }

    const action = this.versionPreviewFullscreen ? "Collapse" : "Expand";
    const label = `${action} workflow preview`;

    this.versionPreviewFullscreenButtonTarget.title = label;
    this.versionPreviewFullscreenButtonTarget.setAttribute("aria-label", label);
    this.versionPreviewFullscreenButtonTarget.dataset.fullscreen = this.versionPreviewFullscreen
      ? "true"
      : "false";
  }

  resizePreviewAfterLayoutChange() {
    window.requestAnimationFrame(() => {
      this.resizePreview();
      window.requestAnimationFrame(() => this.resizePreview());
    });
  }

  resizePreview() {
    this.previewController()?.resize();
  }

  previewController() {
    if (!this.hasVersionsDialogTarget) {
      return null;
    }

    const previewElement = this.versionsDialogTarget.querySelector(
      "[data-workflow-editor-preview-root]",
    );
    if (!previewElement) {
      return null;
    }

    return this.application.getControllerForElementAndIdentifier(previewElement, "workflow-editor");
  }

  patchCanvasDrawDevicePixelRatio() {
    const originalDraw = this.canvas.draw.bind(this.canvas);

    this.canvas.draw = (...args) => {
      const descriptor = Object.getOwnPropertyDescriptor(window, "devicePixelRatio");
      const devicePixelRatio = window.devicePixelRatio || 1;

      try {
        Object.defineProperty(window, "devicePixelRatio", {
          configurable: true,
          get: () => Math.max(1, devicePixelRatio),
        });
      } catch {
        originalDraw(...args);
        return;
      }

      try {
        originalDraw(...args);
      } finally {
        if (descriptor) {
          Object.defineProperty(window, "devicePixelRatio", descriptor);
        } else {
          delete window.devicePixelRatio;
        }
      }
    };
  }

  patchCanvasPrompt() {
    this.canvas.prompt = (title, value, callback, event, multiline) => {
      if (this.readonlyValue) {
        return null;
      }

      const dialog = document.createElement("div");
      dialog.className = "graphdialog rounded";
      dialog.is_modified = false;
      dialog.innerHTML = multiline
        ? "<span class='name'></span> <textarea autofocus class='value'></textarea><button class='rounded'>OK</button>"
        : "<span class='name'></span> <input autofocus type='text' class='value'/><button class='rounded'>OK</button>";

      dialog.close = () => {
        this.canvas.prompt_box = null;
        dialog.remove();
      };

      const nameElement = dialog.querySelector(".name");
      const valueElement = dialog.querySelector(".value");
      const button = dialog.querySelector("button");
      if (!nameElement || !valueElement || !button) return null;

      nameElement.textContent = title || "";
      valueElement.value = value;

      const submit = () => {
        callback?.(valueElement.value);
        this.canvas.setDirty(true);
        dialog.close();
      };

      valueElement.addEventListener("keydown", (keydownEvent) => {
        dialog.is_modified = true;

        if (keydownEvent.key === "Escape") {
          dialog.close();
        } else if (keydownEvent.key === "Enter" && keydownEvent.target.localName !== "textarea") {
          submit();
        } else {
          return;
        }

        keydownEvent.preventDefault();
        keydownEvent.stopPropagation();
      });

      button.addEventListener("click", submit);

      this.canvas.prompt_box?.close();
      this.canvas.prompt_box = dialog;
      this.canvas.canvas.parentNode?.append(dialog);

      const rect = this.canvas.canvas.getBoundingClientRect();
      let offsetX = -20;
      let offsetY = -20;
      if (rect) {
        offsetX -= rect.left;
        offsetY -= rect.top;
      }

      if (event) {
        dialog.style.left = `${event.clientX + offsetX}px`;
        dialog.style.top = `${event.clientY + offsetY}px`;
      } else {
        dialog.style.left = `${this.canvas.canvas.width * 0.5 + offsetX}px`;
        dialog.style.top = `${this.canvas.canvas.height * 0.5 + offsetY}px`;
      }

      setTimeout(() => {
        valueElement.focus();
        const caretPosition = valueElement.value.length;
        valueElement.setSelectionRange?.(caretPosition, caretPosition);
      }, 10);

      return dialog;
    };
  }

  connect() {
    if (this.hasFactsValue) {
      facts = this.factsValue;
    }
    callGroupCatalog = {
      groups: this.hasCallGroupsValue ? this.callGroupsValue : [],
      loaded: this.hasCallGroupsLoadedValue ? this.callGroupsLoadedValue : false,
    };

    this.armedShelfNode = null;
    this.shelfOpen = false;
    this.inFlight = false;
    this.selectedVersion = null;
    this.versionPreviewRequestToken = 0;
    this.requestedVersionId = null;
    this.fullscreen = false;
    this.versionPreviewFullscreen = false;
    this.importOpenedFromStartingPoint = false;
    this.handleCanvasPointerDown = this.handleCanvasPointerDown.bind(this);
    this.handleCanvasContextMenu = this.handleCanvasContextMenu.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.recoverCanvasInteraction = this.recoverCanvasInteraction.bind(this);
    this.handleDocumentVisibilityChange = this.handleDocumentVisibilityChange.bind(this);

    this.graph = new LGraph();

    this.canvas = new LGraphCanvas(this.canvasTarget, this.graph);
    // LiteGraph's separate background canvas can leave compositing artifacts, and its draw path
    // treats browser zoom below 100% as a DPR below 1. Render on one canvas and clamp DPR while
    // drawing so the editor stays sharp without shrinking the interactive area.
    this.canvas.bgcanvas = this.canvas.canvas;
    this.canvas.bgctx = this.canvas.ctx;
    this.patchCanvasDrawDevicePixelRatio();
    this.patchCanvasPrompt();

    this.canvas.allow_searchbox = false;
    this.canvas.read_only = this.readonlyValue;
    this.canvas.default_connection_color_byType = {
      intent: "#4fe355",
      flow: "#51c6de",
    };
    this.canvas.default_connection_color_byTypeOff = {
      intent: "#C8E6C9",
      flow: "#c5d2e9",
    };
    this.canvas.show_info = false;
    this.canvas.render_canvas_border = false;
    this.canvas.zoom_modify_alpha = false;
    this.canvas.getMenuOptions = () => null;
    this.canvas.getNodeMenuOptions = this.readonlyValue
      ? () => null
      : (node) => [
          {
            content: "Clone",
            disabled: node.clonable === false,
            callback: LGraphCanvas.onMenuNodeClone,
          },
          {
            content: "Remove",
            disabled: !(node.removable !== false && !node.block_delete),
            callback: LGraphCanvas.onMenuNodeRemove,
          },
        ];

    this.canvasTarget.addEventListener("pointerdown", this.handleCanvasPointerDown, true);
    this.canvasTarget.addEventListener("contextmenu", this.handleCanvasContextMenu, true);
    document.addEventListener("keydown", this.handleDocumentKeydown);
    document.addEventListener("pointerup", this.recoverCanvasInteraction);
    document.addEventListener("pointercancel", this.recoverCanvasInteraction);
    document.addEventListener("visibilitychange", this.handleDocumentVisibilityChange);
    window.addEventListener("blur", this.recoverCanvasInteraction);

    this.parse();
    if (!this.readonlyValue && !this.previewValue) {
      this.renderNodeShelf();
      this.shelfOpen = this.graph.nodes.length === 0;
    }
    this.updateShelfDisplay();
    this.updateFullscreenButton();
    this.updateVersionPreviewFullscreenButton();
    this.resize();
    this.rememberSavedState();
    if (!this.readonlyValue) {
      this.updateControls();
      this.dirtyInterval = window.setInterval(() => this.updateControls(), 1000);
    }
    if (this.showStartingPointValue && this.hasStartingPointDialogTarget) {
      window.requestAnimationFrame(() => this.openStartingPointDialog());
    }
  }

  disconnect() {
    this.versionPreviewRequestToken += 1;
    this.requestedVersionId = null;
    this.recoverCanvasInteraction();
    this.exitFullscreen();
    this.exitVersionPreviewFullscreen();
    this.canvasTarget.removeEventListener("pointerdown", this.handleCanvasPointerDown, true);
    this.canvasTarget.removeEventListener("contextmenu", this.handleCanvasContextMenu, true);
    document.removeEventListener("keydown", this.handleDocumentKeydown);
    document.removeEventListener("pointerup", this.recoverCanvasInteraction);
    document.removeEventListener("pointercancel", this.recoverCanvasInteraction);
    document.removeEventListener("visibilitychange", this.handleDocumentVisibilityChange);
    window.removeEventListener("blur", this.recoverCanvasInteraction);
    window.clearInterval(this.dirtyInterval);
  }

  parse() {
    if (!this.dataTarget?.value) {
      return;
    }

    const workflow = JSON.parse(this.dataTarget.value);

    if (!workflow) {
      return;
    }

    if (!workflow["nodes"] || !workflow["flows"] || !workflow["intents"]) {
      console.error("workflow must have nodes, flows, and intents");
      return;
    }

    for (const data of workflow["nodes"]) {
      const node = LiteGraph.createNode(`nodes/${data.kind}`, null, { id: data?.id });
      if (!node) {
        console.error(`Can't lookup nodes/${data.kind}`);
        continue;
      }
      node.pos = data?.pos.split(",").map((v) => parseInt(v, 10));

      node.applySerializedOptions(data?.options);

      this.graph.add(node);
    }

    for (const data of workflow["flows"]) {
      const nodes = this.getConnectedNodes(data?.from, data?.to);

      if (nodes) {
        const [fromNode, toNode] = nodes;
        const outputSlot = fromNode.findOutputSlot(data?.name);
        fromNode.connect(outputSlot, toNode, data?.toSlot);
      }
    }

    for (const data of workflow["intents"]) {
      const node = LiteGraph.createNode("special/intent");
      node.pos = data?.pos.split(",").map((v) => parseInt(v, 10));

      node.setProperty("intent", data?.intent);

      this.graph.add(node);

      const nodes = this.getConnectedNodes(data?.from, data?.to);
      if (nodes) {
        const [fromNode, toNode] = nodes;
        const outputSlot = fromNode.findOutputSlot(data?.name);
        fromNode.connect(outputSlot, node, 0);
        node.connect(0, toNode, data?.toSlot);
      }
    }
  }

  getConnectedNodes(fromId, toId) {
    const fromNode = this.graph.getNodeById(this.normalizeTransferNodeId(fromId));
    if (!fromNode) {
      console.error(`Failed to find fromNode: ${fromId}`);
      return null;
    }

    const toNode = this.graph.getNodeById(this.normalizeTransferNodeId(toId));
    if (!toNode) {
      console.error(`Failed to find toNode: ${toId}`);
      return null;
    }

    return [fromNode, toNode];
  }

  buildSerializedWorkflow(options = {}) {
    let serialized = {
      nodes: [],
      flows: [],
      intents: [],
    };

    const nodes = this.graph.nodes.filter(
      (n) => n.constructor.kind && n.constructor.kind !== "intent",
    );

    const errors = options.validate === false ? [] : this.validate(nodes);
    if (errors.length > 0) {
      return { errors };
    }

    for (const node of nodes) {
      const Node = node.constructor;
      const pos = serializePosition(node.pos);
      if (!pos) {
        return { errors: [`${node.displayType} has an invalid position.`] };
      }

      let data = {
        kind: Node.kind,
        pos,
        id: node.id,
      };

      const options = node.serializeOptions();
      if (Object.keys(options).length > 0) {
        data["options"] = options;
      }

      serialized["nodes"].push(data);
    }

    const links = Array.from(this.graph.links.values());
    const flows = links.filter((l) => l.type === "flow");
    const intents = links.filter((l) => l.type === "intent");

    for (const flow of flows) {
      const fromNode = this.graph.getNodeById(flow.origin_id);
      if (fromNode.constructor.kind === "intent") {
        continue;
      }

      const output = fromNode.outputs[flow.origin_slot];

      serialized["flows"].push({
        from: flow.origin_id,
        name: output.name,
        to: flow.target_id,
        toSlot: flow.target_slot,
      });
    }

    for (const intent of intents) {
      const fromNode = this.graph.getNodeById(intent.origin_id);
      const intentNode = this.graph.getNodeById(intent.target_id);
      const flows = intentNode.outputs[0]?.links;

      const output = fromNode.outputs[intent.origin_slot];

      if (!flows || flows.length === 0) {
        continue;
      } else if (flows.length > 1) {
        console.error("Flow connects to too many nodes, only using the first.");
      }

      // Only use the first flow link since flows are only allowed to connect to one place.
      const flow = this.graph.getLink(flows[0]);
      const pos = serializePosition(intentNode.pos);
      if (!pos) {
        return { errors: [`${intentNode.displayType} has an invalid position.`] };
      }

      serialized["intents"].push({
        intent: intentNode.properties.intent,
        pos,
        from: intent.origin_id,
        name: output.name,
        to: flow.target_id,
        toSlot: flow.target_slot,
      });
    }

    return { workflow: serialized, errors: [] };
  }

  generate(event) {
    if (this.readonlyValue) {
      event?.preventDefault();
      return;
    }

    const { workflow, errors } = this.buildSerializedWorkflow();
    if (errors.length > 0) {
      event?.preventDefault();
      window.alert(errors.join("\n"));
      return;
    }

    this.dataTarget.value = JSON.stringify(workflow);
  }

  markDirty() {
    if (this.readonlyValue) {
      return;
    }

    this.updateControls();
  }

  currentWorkflowJson() {
    const { workflow, errors } = this.buildSerializedWorkflow({ validate: false });
    if (errors.length > 0) {
      return this.dataTarget.value || "";
    }

    return JSON.stringify(workflow);
  }

  currentName() {
    return this.hasNameTarget ? this.nameTarget.value : "";
  }

  isDirty() {
    return (
      this.currentName() !== this.savedName || this.currentWorkflowJson() !== this.savedWorkflowJson
    );
  }

  hasSavedDraft() {
    return this.versionStatusValue === "DRAFT" && this.hasVersionIdValue;
  }

  rememberSavedState() {
    this.savedName = this.currentName();
    this.savedWorkflowJson = this.currentWorkflowJson();
  }

  updateControls() {
    if (this.readonlyValue) {
      return;
    }

    const dirty = this.isDirty();

    if (this.hasSaveDraftButtonTarget) {
      this.saveDraftButtonTarget.disabled = this.inFlight || !dirty;
    }

    if (this.hasPublishButtonTarget) {
      this.publishButtonTarget.disabled = this.inFlight || dirty || !this.hasSavedDraft();
    }

    if (this.hasDiscardDraftButtonTarget) {
      this.discardDraftButtonTarget.disabled = this.inFlight || (!dirty && !this.hasSavedDraft());
    }

    if (this.hasStatusChipTarget) {
      let label = this.hasWorkflowIdValue
        ? !this.hasVersionIdValue
          ? "No draft"
          : this.versionStatusValue === "DRAFT"
            ? "Draft"
            : "Published"
        : "Unsaved";
      if (dirty) label = "Unsaved";
      if (this.inFlightState === "saving") label = "Saving";
      if (this.inFlightState === "publishing") label = "Publishing";
      if (this.inFlightState === "discarding") label = "Discarding";

      this.statusChipTarget.textContent = label;
      this.statusChipTarget.dataset.status = label.toLowerCase();
    }

    if (this.hasSaveStatusTarget && !this.inFlightState) {
      if (dirty) {
        this.saveStatusTarget.textContent = "Unsaved";
      } else if (this.versionStatusValue === "DRAFT" && this.versionUpdatedAtValue) {
        this.saveStatusTarget.textContent = `Saved ${this.relativeTime(this.versionUpdatedAtValue)}`;
      } else if (this.versionStatusValue === "PUBLISHED" && this.versionPublishedAtValue) {
        this.saveStatusTarget.textContent = `Published ${this.relativeTime(this.versionPublishedAtValue)}`;
      } else {
        this.saveStatusTarget.textContent = "";
      }
    }
  }

  async saveDraft(event) {
    event?.preventDefault();
    if (this.readonlyValue) {
      return;
    }

    await this.persistDraft();
  }

  async persistDraft() {
    if (this.readonlyValue) {
      return;
    }

    const { workflow, errors } = this.buildSerializedWorkflow();
    if (errors.length > 0) {
      window.alert(errors.join("\n"));
      return;
    }

    this.clearAlert();
    this.inFlight = true;
    this.inFlightState = "saving";
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saving...";
    this.updateControls();

    const payload = {
      name: this.currentName(),
      data: workflow,
    };

    if (this.hasSavedDraft()) {
      payload.draftVersionId = this.versionIdValue;
      payload.revision = this.versionRevisionValue;
    }

    try {
      const result = await this.requestJson(this.draftUrlValue, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      this.applyWorkflow(result.workflow);
      this.applyVersion(result.version);
      this.dataTarget.value = JSON.stringify(result.version.data);
      this.rememberSavedState();
    } catch (error) {
      this.handleRequestError(error, "Draft could not be saved.");
    } finally {
      this.inFlight = false;
      this.inFlightState = null;
      this.updateControls();
    }
  }

  async publish(event) {
    event?.preventDefault();
    if (this.readonlyValue) {
      return;
    }

    if (!this.hasSavedDraft() || this.isDirty()) {
      return;
    }

    const { errors } = this.buildSerializedWorkflow();
    if (errors.length > 0) {
      window.alert(errors.join("\n"));
      return;
    }

    this.clearAlert();
    this.inFlight = true;
    this.inFlightState = "publishing";
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Publishing...";
    this.updateControls();

    try {
      const result = await this.requestJson(this.publishUrlValue, {
        method: "POST",
        body: JSON.stringify({
          draftVersionId: this.versionIdValue,
          revision: this.versionRevisionValue,
        }),
      });

      this.applyVersion(result.version);
      this.dataTarget.value = JSON.stringify(result.version.data);
      this.rememberSavedState();
    } catch (error) {
      this.handleRequestError(error, "Draft could not be published.");
    } finally {
      this.inFlight = false;
      this.inFlightState = null;
      this.updateControls();
    }
  }

  async discardDraft(event) {
    event?.preventDefault();
    if (this.readonlyValue || (!this.hasSavedDraft() && !this.isDirty())) {
      return;
    }

    const message = this.hasSavedDraft()
      ? "Discard the current draft?"
      : "Discard unsaved changes?";
    if (!window.confirm(message)) {
      return;
    }

    if (!this.hasSavedDraft()) {
      this.discardLocalChanges();
      return;
    }

    this.clearAlert();
    this.inFlight = true;
    this.inFlightState = "discarding";
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Discarding...";
    this.updateControls();

    try {
      const result = await this.requestJson(this.draftUrlValue, {
        method: "DELETE",
        body: JSON.stringify({
          draftVersionId: this.versionIdValue,
          revision: this.versionRevisionValue,
        }),
      });

      this.applyWorkflow(result.workflow);
      this.applyVersion(result.version);
      this.loadWorkflowData(result.version?.data ?? result.data);
      this.rememberSavedState();
    } catch (error) {
      this.handleRequestError(error, "Draft could not be discarded.");
    } finally {
      this.inFlight = false;
      this.inFlightState = null;
      this.updateControls();
    }
  }

  discardLocalChanges() {
    if (this.hasNameTarget) {
      this.nameTarget.value = this.savedName || "";
    }

    this.loadWorkflowData(this.savedWorkflowJson ? JSON.parse(this.savedWorkflowJson) : null);
    this.updateControls();
  }

  async openVersions(event) {
    event?.preventDefault();
    this.recoverCanvasInteraction();
    this.clearAlert();
    this.versionsDialogTarget.showModal();
    await this.loadVersions();
  }

  closeActionsMenu(event) {
    event?.currentTarget
      ?.closest(".workflow-actions-menu")
      ?.querySelector('[data-menu-target="menu"]')
      ?.classList.remove("show-menu");
  }

  openImportDialog(event) {
    event?.preventDefault();
    this.importOpenedFromStartingPoint = false;
    this.clearImportValidation();
    this.openTransferDialog(this.importDialogTarget, this.importTextareaTarget);
  }

  openImportDialogFromStartingPoint(event) {
    event?.preventDefault();
    this.importOpenedFromStartingPoint = true;
    this.startingPointDialogTarget.close();
    this.clearImportValidation();
    this.openTransferDialog(this.importDialogTarget, this.importTextareaTarget);
  }

  useStartingPointTemplate(event) {
    event?.preventDefault();
    const template = this.templatesValue.find(
      ({ id }) => id === event.currentTarget.dataset.templateId,
    );
    if (!template) {
      this.showAlert("The selected workflow template is unavailable.");
      return;
    }

    const transferJson = JSON.stringify({
      format: WORKFLOW_TRANSFER_FORMAT,
      formatVersion: WORKFLOW_TRANSFER_FORMAT_VERSION,
      data: template.data,
    });
    const result = this.importTransferJson(transferJson);
    if (result.error) {
      this.showAlert(result.error);
      return;
    }
    if (!result.applied) return;

    this.startingPointDialogTarget.close();
  }

  startEmptyWorkflow(event) {
    event?.preventDefault();
    this.startingPointDialogTarget.close();
  }

  startingPointDialogClosed() {
    this.recoverCanvasInteraction();
    this.resizeAfterLayoutChange();
  }

  openExportDialog(event) {
    event?.preventDefault();
    const { workflow, errors } = this.buildSerializedWorkflow({ validate: false });
    if (errors.length > 0) {
      this.showAlert(errors.join("\n"));
      return;
    }

    this.prepareExport(workflow);
    this.openTransferDialog(this.exportDialogTarget, this.exportTextareaTarget);
  }

  openVersionExportDialog(event) {
    event?.preventDefault();
    if (!this.selectedVersion) return;

    this.prepareExport(this.selectedVersion.data);
    this.openTransferDialog(this.exportDialogTarget, this.exportTextareaTarget);
  }

  prepareExport(workflow) {
    this.exportTextareaTarget.value = JSON.stringify(
      {
        format: WORKFLOW_TRANSFER_FORMAT,
        formatVersion: WORKFLOW_TRANSFER_FORMAT_VERSION,
        name: this.currentName(),
        data: workflow,
      },
      null,
      2,
    );
    this.copyFeedbackTarget.textContent = "";
  }

  async copyExportJson(event) {
    event?.preventDefault();
    let copied = false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(this.exportTextareaTarget.value);
        copied = true;
      } catch (_error) {
        copied = false;
      }
    }

    if (!copied) {
      this.exportTextareaTarget.focus();
      this.exportTextareaTarget.select();
      try {
        copied = document.execCommand("copy");
      } catch (_error) {
        copied = false;
      }
    }

    this.copyFeedbackTarget.textContent = copied
      ? "Workflow JSON copied."
      : "Copy failed. Select the JSON and copy it manually.";
  }

  useImportedJson(event) {
    event?.preventDefault();
    const result = this.importTransferJson(this.importTextareaTarget.value);
    if (result.error) {
      this.showImportValidation(result.error);
      return;
    }
    if (!result.applied) return;

    this.importOpenedFromStartingPoint = false;
    this.importTextareaTarget.value = "";
    this.clearImportValidation();
    this.importDialogTarget.close();
  }

  importTransferJson(value) {
    const result = this.parseTransferJson(value);
    if (result.error) return result;

    if (
      (this.hasSavedDraft() || this.isDirty()) &&
      !window.confirm("Replace the current workflow with the imported JSON?")
    ) {
      return { applied: false };
    }

    this.loadWorkflowData(result.workflow);
    this.updateControls();
    return { applied: true };
  }

  parseTransferJson(value) {
    let parsed;
    try {
      parsed = JSON.parse(value);
    } catch (_error) {
      return { error: "Workflow JSON is malformed." };
    }

    if (!this.isRecord(parsed)) {
      return { error: "Workflow JSON must contain an object." };
    }

    const envelopeKeys = ["format", "formatVersion", "data"];
    const isEnvelope = envelopeKeys.some((key) => Object.hasOwn(parsed, key));
    let workflow = parsed;

    if (isEnvelope) {
      if (parsed.format !== WORKFLOW_TRANSFER_FORMAT) {
        return { error: `Unsupported workflow format: ${String(parsed.format)}.` };
      }
      if (parsed.formatVersion !== WORKFLOW_TRANSFER_FORMAT_VERSION) {
        return { error: `Unsupported workflow format version: ${String(parsed.formatVersion)}.` };
      }
      if (!this.isRecord(parsed.data)) {
        return { error: "Workflow envelope data must be an object." };
      }

      workflow = parsed.data;
    }

    const error = this.validateTransferWorkflow(workflow);
    return error ? { error } : { workflow };
  }

  validateTransferWorkflow(workflow) {
    if (!this.isRecord(workflow)) {
      return "Workflow data must be an object.";
    }

    for (const property of ["nodes", "flows", "intents"]) {
      if (!Array.isArray(workflow[property])) {
        return `Workflow data must include a ${property} array.`;
      }
    }

    const graph = new LGraph();
    const nodesById = new Map();

    for (const [index, data] of workflow.nodes.entries()) {
      if (!this.isRecord(data)) {
        return `Node ${index + 1} must be an object.`;
      }
      if (!this.validTransferNodeId(data.id)) {
        if (this.reservedTransferNodeId(data.id)) {
          return `Node ${index + 1} uses reserved ID ${String(data.id)}.`;
        }
        return `Node ${index + 1} must have a non-empty ID.`;
      }
      const normalizedId = this.normalizeTransferNodeId(data.id);
      if (nodesById.has(normalizedId)) {
        return `Node ID ${String(data.id)} is duplicated.`;
      }
      if (!SUPPORTED_WORKFLOW_NODE_KINDS.has(data.kind)) {
        return `Node ${String(data.id)} has unsupported kind ${String(data.kind)}.`;
      }
      const position = this.parseTransferPosition(data.pos);
      if (!position) {
        return `Node ${String(data.id)} has an invalid position.`;
      }
      if (data.options !== undefined && !this.isRecord(data.options)) {
        return `Node ${String(data.id)} options must be an object.`;
      }

      try {
        const node = LiteGraph.createNode(`nodes/${data.kind}`, null, { id: data.id });
        if (!node) {
          return `Node ${String(data.id)} has unsupported kind ${String(data.kind)}.`;
        }
        node.pos = position;
        node.applySerializedOptions(data.options);
        graph.add(node);
        nodesById.set(normalizedId, node);
      } catch (_error) {
        return `Node ${String(data.id)} has invalid options.`;
      }
    }

    for (const [index, flow] of workflow.flows.entries()) {
      const error = this.validateTransferConnection(flow, nodesById, "flow", index);
      if (error) return error;
    }

    for (const [index, intent] of workflow.intents.entries()) {
      if (!this.isRecord(intent)) {
        return `Intent ${index + 1} must be an object.`;
      }
      if (typeof intent.intent !== "string") {
        return `Intent ${index + 1} must include intent text.`;
      }
      if (!this.parseTransferPosition(intent.pos)) {
        return `Intent ${index + 1} has an invalid position.`;
      }
      const error = this.validateTransferConnection(intent, nodesById, "intent", index);
      if (error) return error;
    }

    return null;
  }

  validateTransferConnection(connection, nodesById, type, index) {
    const label = type === "flow" ? "Flow" : "Intent";
    if (!this.isRecord(connection)) {
      return `${label} ${index + 1} must be an object.`;
    }

    const fromNode = nodesById.get(this.normalizeTransferNodeId(connection.from));
    const toNode = nodesById.get(this.normalizeTransferNodeId(connection.to));
    if (!fromNode) {
      return `${label} ${index + 1} references missing source node ${String(connection.from)}.`;
    }
    if (!toNode) {
      return `${label} ${index + 1} references missing target node ${String(connection.to)}.`;
    }
    if (typeof connection.name !== "string") {
      return `${label} ${index + 1} must include an output name.`;
    }

    const outputSlot = fromNode.findOutputSlot(connection.name);
    const output = fromNode.outputs[outputSlot];
    if (outputSlot < 0 || output?.type !== type) {
      return `${label} ${index + 1} references invalid ${type} output ${connection.name}.`;
    }
    if (!Number.isInteger(connection.toSlot) || connection.toSlot < 0) {
      return `${label} ${index + 1} has an invalid target slot.`;
    }
    const input = toNode.inputs[connection.toSlot];
    if (!input || input.type !== "flow") {
      return `${label} ${index + 1} references invalid target slot ${String(connection.toSlot)}.`;
    }

    return null;
  }

  validTransferNodeId(id) {
    const validType =
      (typeof id === "string" && id.trim() !== "") ||
      (typeof id === "number" && Number.isFinite(id));

    return validType && !this.reservedTransferNodeId(id);
  }

  reservedTransferNodeId(id) {
    return id === -1 || (typeof id === "string" && id.trim() !== "" && Number(id) === -1);
  }

  normalizeTransferNodeId(id) {
    return id === null || id === undefined ? null : String(id);
  }

  parseTransferPosition(position) {
    if (typeof position !== "string" || !SERIALIZED_POSITION_PATTERN.test(position)) {
      return null;
    }

    const coordinates = position.split(",").map(Number);
    return coordinates.every(Number.isFinite) ? coordinates : null;
  }

  isRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  showImportValidation(message) {
    this.importValidationMessageTarget.textContent = message;
    this.importValidationMessageTarget.classList.remove("hidden");
  }

  clearImportValidation() {
    if (!this.hasImportValidationMessageTarget) return;

    this.importValidationMessageTarget.textContent = "";
    this.importValidationMessageTarget.classList.add("hidden");
  }

  openTransferDialog(dialog, focusTarget) {
    this.recoverCanvasInteraction();
    this.previewController()?.recoverCanvasInteraction();
    const controller = this.application.getControllerForElementAndIdentifier(dialog, "dialog");
    controller?.openDialog(dialog);
    window.requestAnimationFrame(() => focusTarget.focus());
  }

  openStartingPointDialog() {
    this.openTransferDialog(this.startingPointDialogTarget, this.startingPointEmptyButtonTarget);
  }

  closeTransferDialog(event) {
    event?.preventDefault();
    event?.currentTarget?.closest("dialog")?.close();
  }

  transferDialogClosed() {
    this.recoverCanvasInteraction();
    this.previewController()?.recoverCanvasInteraction();
    this.resizeAfterLayoutChange();
    if (this.hasVersionsDialogTarget && this.versionsDialogTarget.open) {
      this.resizePreviewAfterLayoutChange();
    }
  }

  importDialogClosed() {
    this.transferDialogClosed();
    if (!this.importOpenedFromStartingPoint) return;

    this.importOpenedFromStartingPoint = false;
    window.requestAnimationFrame(() => this.openStartingPointDialog());
  }

  async loadVersions() {
    this.versionPreviewRequestToken += 1;
    this.requestedVersionId = null;
    this.versionsListTarget.replaceChildren();
    this.versionsEmptyTarget.classList.add("hidden");
    this.versionPreviewTitleTarget.textContent = "Select a version";
    this.loadPreviewWorkflowData(null);
    this.restoreVersionButtonTarget.disabled = true;
    this.exportVersionButtonTarget.disabled = true;
    this.selectedVersion = null;

    try {
      const result = await this.requestJson(this.versionsUrlValue);
      const versions = this.sortVersionsByMostRecent(result.versions || []);
      this.versionsEmptyTarget.classList.toggle("hidden", versions.length > 0);

      for (const version of versions) {
        const item = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = `Version ${version.versionNumber || ""}`;
        button.dataset.versionId = version.id;
        button.addEventListener("click", () => this.previewVersion(version.id));

        const meta = document.createElement("small");
        meta.textContent = version.publishedAt
          ? `Published ${this.relativeTime(version.publishedAt)}`
          : "";

        item.append(button, meta);
        this.versionsListTarget.appendChild(item);
      }

      if (versions[0]) {
        await this.previewVersion(versions[0].id);
      }
    } catch (error) {
      this.handleRequestError(error, "Versions could not be loaded.");
    }
  }

  sortVersionsByMostRecent(versions) {
    return [...versions].sort((a, b) => {
      const publishedDiff = Date.parse(b.publishedAt || 0) - Date.parse(a.publishedAt || 0);
      if (publishedDiff !== 0) {
        return publishedDiff;
      }

      return (b.versionNumber || 0) - (a.versionNumber || 0);
    });
  }

  async previewVersion(versionId) {
    const requestToken = ++this.versionPreviewRequestToken;
    const requestedVersionId = String(versionId);
    this.requestedVersionId = requestedVersionId;
    this.selectedVersion = null;
    this.markSelectedVersionButton(null);
    this.versionPreviewTitleTarget.textContent = "Loading version...";
    this.loadPreviewWorkflowData(null);
    this.restoreVersionButtonTarget.disabled = true;
    this.exportVersionButtonTarget.disabled = true;

    try {
      const result = await this.requestJson(`${this.versionsUrlValue}/${versionId}`);
      if (
        requestToken !== this.versionPreviewRequestToken ||
        requestedVersionId !== this.requestedVersionId
      ) {
        return;
      }

      this.selectedVersion = result.version;
      this.markSelectedVersionButton(requestedVersionId);
      this.versionPreviewTitleTarget.textContent = `Version ${this.selectedVersion.versionNumber}`;
      this.loadPreviewWorkflowData(this.selectedVersion.data);
      this.restoreVersionButtonTarget.disabled = false;
      this.exportVersionButtonTarget.disabled = false;
    } catch (error) {
      if (
        requestToken !== this.versionPreviewRequestToken ||
        requestedVersionId !== this.requestedVersionId
      ) {
        return;
      }

      this.requestedVersionId = null;
      this.versionPreviewTitleTarget.textContent = "Select a version";
      this.handleRequestError(error, "Version could not be loaded.");
    }
  }

  markSelectedVersionButton(versionId) {
    for (const button of this.versionsListTarget.querySelectorAll("button[data-version-id]")) {
      const selected = button.dataset.versionId === versionId;
      button.classList.toggle("active", selected);
      button.setAttribute("aria-current", selected ? "true" : "false");
    }
  }

  loadPreviewWorkflowData(data) {
    this.previewController()?.loadWorkflowData(data);
  }

  async restoreSelectedVersion(event) {
    event?.preventDefault();
    if (!this.selectedVersion) {
      return;
    }

    if (this.isDirty()) {
      this.showAlert("Save or discard local changes before using a previous version as the draft.");
      return;
    }

    if (
      this.hasSavedDraft() &&
      !window.confirm("Replace the current draft with this published version?")
    ) {
      return;
    }

    const payload = {};
    if (this.hasSavedDraft()) {
      payload.draftVersionId = this.versionIdValue;
      payload.revision = this.versionRevisionValue;
    }

    this.inFlight = true;
    this.inFlightState = "saving";
    this.updateControls();

    try {
      const result = await this.requestJson(
        `${this.versionsUrlValue}/${this.selectedVersion.id}/draft`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      this.applyVersion(result.version);
      this.loadWorkflowData(result.version.data);
      this.rememberSavedState();
      this.versionsDialogTarget.close();
    } catch (error) {
      this.handleRequestError(error, "Version could not be used as draft.");
    } finally {
      this.inFlight = false;
      this.inFlightState = null;
      this.updateControls();
    }
  }

  loadWorkflowData(data) {
    this.dataTarget.value = data ? JSON.stringify(data) : "";
    this.graph.clear();
    this.parse();
    this.canvas.setDirty(true, true);
    this.resize();
  }

  applyWorkflow(workflow) {
    if (workflow?.name && this.hasNameTarget) {
      this.nameTarget.value = workflow.name;
    }
  }

  applyVersion(version) {
    if (!version) {
      this.versionIdValue = undefined;
      this.versionStatusValue = undefined;
      this.versionRevisionValue = undefined;
      this.versionUpdatedAtValue = undefined;
      this.versionPublishedAtValue = undefined;
      return;
    }

    this.versionIdValue = version.id;
    this.versionStatusValue = version.status;
    this.versionRevisionValue = version.revision;
    this.versionUpdatedAtValue = version.updatedAt || "";
    this.versionPublishedAtValue = version.publishedAt || "";
  }

  async requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      ...options,
    });

    if (response.redirected) {
      const error = new Error("Your session has expired. Sign in again to continue.");
      error.status = 401;
      error.redirectUrl = response.url;
      throw error;
    }

    const contentType = response.headers.get("Content-Type") || "";
    const expectsJson = contentType.includes("application/json");
    const data = expectsJson ? await response.json().catch(() => ({})) : {};

    if (!expectsJson) {
      const error = new Error("Unexpected response from server. Reload the page and try again.");
      error.status = response.status;
      throw error;
    }

    if (!response.ok) {
      const error = new Error(data.error || response.statusText);
      error.status = response.status;
      error.redirectUrl = data.redirect_url;
      throw error;
    }

    return data;
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || "";
  }

  handleRequestError(error, fallback) {
    if (error.status === 401) {
      this.showAlert(error.message || "Your session has expired. Sign in again to continue.");
      if (error.redirectUrl) {
        window.location.assign(error.redirectUrl);
      }
    } else if (error.status === 409) {
      this.showAlert("This workflow draft changed elsewhere. Reload the page before continuing.");
    } else {
      this.showAlert(error.message || fallback);
    }
  }

  showAlert(message) {
    if (!this.hasAlertTarget) {
      window.alert(message);
      return;
    }

    this.alertTarget.textContent = message;
    this.alertTarget.classList.remove("hidden");
  }

  clearAlert() {
    if (!this.hasAlertTarget) {
      return;
    }

    this.alertTarget.textContent = "";
    this.alertTarget.classList.add("hidden");
  }

  relativeTime(value) {
    const time = Date.parse(value);
    if (Number.isNaN(time)) {
      return "";
    }

    const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));
    if (seconds < 60) return "just now";

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  validate(nodes) {
    const errors = [];

    for (const node of nodes) {
      switch (node.constructor.kind) {
        case "branch":
          errors.push(...this.validateBranchNode(node));
          break;
        case "route":
          errors.push(...this.validateRouteNode(node));
          break;
        case "schedule":
          errors.push(...this.validateScheduleNode(node));
          break;
        case "callGroup":
          errors.push(...this.validateCallGroupNode(node));
          break;
        case "transfer":
          errors.push(...this.validateTransferNode(node));
          break;
        default:
          break;
      }
    }

    return errors;
  }

  validateBranchNode(node) {
    const errors = [];

    if (!node.properties.fact?.trim()) {
      errors.push(`${node.title} must select a fact.`);
    }

    if (!BRANCH_OPERATORS.includes(node.properties.operator)) {
      errors.push(`${node.title} must select an operator.`);
    }

    if (
      !BRANCH_VALUELESS_OPERATORS.includes(node.properties.operator) &&
      !node.properties.value?.trim()
    ) {
      errors.push(`${node.title} must define a comparison value.`);
    }

    for (const flow of ["then", "else"]) {
      if (!this.hasConnectedOutput(node, flow)) {
        errors.push(`${node.title} must connect its ${flow} path.`);
      }
    }

    return errors;
  }

  validateCallGroupNode(node) {
    const errors = [];
    const callGroupId = node.properties.callGroupId;

    if (typeof callGroupId !== "string" || !callGroupId.trim()) {
      errors.push(`${node.title} must select a call group.`);
    } else if (!CALL_GROUP_UUID_PATTERN.test(callGroupId)) {
      errors.push(`${node.title} must select a call group with a valid UUID.`);
    } else if (!callGroupCatalog.loaded) {
      errors.push(
        `${node.title} cannot validate its call group because the catalog is unavailable.`,
      );
    } else if (!callGroupCatalog.groups.some((group) => group.id === callGroupId)) {
      errors.push(`${node.title} must select an available call group for this organization.`);
    }

    const timeoutSeconds = node.properties.timeoutSeconds;
    if (!Number.isInteger(timeoutSeconds) || timeoutSeconds < 5 || timeoutSeconds > 120) {
      errors.push(`${node.title} timeout must be a whole number from 5 through 120 seconds.`);
    }

    if (!CALL_GROUP_CALLER_ID_CHOICES.some((choice) => choice.value === node.properties.callerId)) {
      errors.push(`${node.title} must select Caller or Assist for caller ID.`);
    }

    if (typeof node.properties.acceptanceScreeningEnabled !== "boolean") {
      errors.push(`${node.title} acceptance screening enabled must be true or false.`);
    }

    if (
      typeof node.properties.acceptanceScreeningKey !== "string" ||
      !/^[0-9]$/.test(node.properties.acceptanceScreeningKey)
    ) {
      errors.push(`${node.title} acceptance key must be one numeric DTMF key.`);
    }

    const message = node.properties.acceptanceScreeningMessage;
    if (typeof message !== "string" || !message.trim()) {
      errors.push(`${node.title} must define an acceptance message.`);
    } else if (CALL_GROUP_LIQUID_DELIMITER_PATTERN.test(message.replaceAll("{{ caller }}", ""))) {
      errors.push(`${node.title} acceptance message may only use exact {{ caller }} outputs.`);
    }

    if (!this.hasConnectedOutput(node, "unanswered")) {
      errors.push(`${node.title} must connect its unanswered path.`);
    }

    return errors;
  }

  validateTransferNode(node) {
    return normalizePhoneNumberToE164(node.properties.phone)
      ? []
      : [`${node.title} must define a valid phone number.`];
  }

  validateRouteNode(node) {
    const errors = [];
    const values = new Set();
    let cases = parseInt(node.properties.cases, 10);
    if (Number.isNaN(cases)) {
      cases = 0;
    }

    if (!node.properties.fact?.trim()) {
      errors.push(`${node.title} must select a fact.`);
    }

    if (cases < 1) {
      errors.push(`${node.title} must define at least one match value.`);
    }

    for (let i = 0; i < cases; i++) {
      const label = String(i + 1);
      const value = node.properties[`case${i}`]?.trim();

      if (!value) {
        errors.push(`${node.title} match ${label} must define a value.`);
        continue;
      }

      if (values.has(value)) {
        errors.push(`${node.title} match ${label} duplicates another match value.`);
      }
      values.add(value);

      if (!this.hasConnectedOutput(node, String(i))) {
        errors.push(`${node.title} match ${label} must connect to a node.`);
      }
    }

    if (!this.hasConnectedOutput(node, "default")) {
      errors.push(`${node.title} must connect its default path.`);
    }

    return errors;
  }

  validateScheduleNode(node) {
    const errors = [];
    const selectedDays = SCHEDULE_VALID_DAYS.filter((day) => node.properties[day]);

    if (!validScheduleTime(node.properties.startTime)) {
      errors.push(`${node.title} must define a valid start time.`);
    }

    if (!validScheduleTime(node.properties.endTime)) {
      errors.push(`${node.title} must define a valid end time.`);
    }

    if (
      validScheduleTime(node.properties.startTime) &&
      validScheduleTime(node.properties.endTime) &&
      node.properties.startTime === node.properties.endTime
    ) {
      errors.push(`${node.title} start and end times must be different.`);
    }

    if (selectedDays.length === 0) {
      errors.push(`${node.title} must select at least one day.`);
    }

    if (!validScheduleTimezone(node.properties.timezone)) {
      errors.push(`${node.title} must select a valid timezone.`);
    }

    for (const flow of ["active", "inactive"]) {
      if (!this.hasConnectedOutput(node, flow)) {
        errors.push(`${node.title} must connect its ${flow} path.`);
      }
    }

    return errors;
  }

  hasConnectedOutput(node, name) {
    const output = node.outputs?.find((o) => o.name === name);
    return (output?.links || []).length > 0;
  }
}
